import React, { useMemo, useEffect, useState } from 'react';
import {
	TextField,
	FormLabel,
	Tooltip,
	CircularProgress,
	FormHelperText,
	useTheme,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import {
	useGetClientPropertiesQuery,
	useUpdateClientRowMutation,
	useUpdateClientxMutation,
} from '@/features/clients/clientsApi';
import { createValidationSchema } from './clientDetailData';
import { IndustrySelect } from './industrySelect';
import { DrawNumeric } from './drawNumeric';
import { ResetButton } from './resetButton';
import { useParams } from 'react-router-dom';
import { AnzsicCodeSearch } from './anzsicCodeSearch';
import { SourceOfBusinessSelect } from './sourceOfBusinessSelect';
import { ClientStatusSelect } from '../selects/ClientStatusSelect';
import { ReferralPartnerSelect } from '../selects/ReferralPartnerSelect';
import { showToast } from '@/features/toast/toastSlice';

const insightFields = {
	address: 'AddressLine1',
	city: 'City',
	Postcode: 'Postcode',
	Suburb: 'Suburb',
	tradingAs: 'TradingAs',
	sourceOfBusiness: 'SourceOfBusinessId',
	standardIndustryCode: 'StandardIndustryCode',
	Addressee: 'Addressee',
	isCurrent: 'CurrentStatusId',
};

export const ClientDetailField = ({ header, title = '', disabled = false }) => {
	// The client's name has its own dedicated rename flow — this generic field
	// must never be able to update it.
	const isReadOnly = ['name', 'email'].includes(header);

	const theme = useTheme();
	const params = useParams();
	const [error, setError] = useState(false);

	const [updateClientRow, clientRowState] = useUpdateClientRowMutation();
	const [updateClientx, clientInsightState] = useUpdateClientxMutation();

	const dispatch = useDispatch();

	const clientId = params?.clientId;

	const { data: clientProps, ...clientQuery } = useGetClientPropertiesQuery(clientId, {
		skip: !clientId,
	});
	const clientRow = clientProps?.clientRow;
	const insight = clientProps?.insight;

	const isUpdating = clientRowState.isLoading || clientInsightState.isLoading;
	const initialValue = [
		'description',
		'isCurrent',
		'referralPartner',
		'numberOfEmployees',
		'annualRevenue',
		'website',
	].includes(header)
		? clientRow?.[header]
		: (clientRow?.[header] ?? insight?.[insightFields[header] ?? header]);

	const [value, setValue] = useState(() => initialValue);

	useEffect(() => {
		if (initialValue != null) {
			setValue(initialValue);
		}
	}, [clientId]);

	const isLoading = clientQuery.isLoading || clientQuery.isUninitialized;

	const loading = isLoading || isUpdating;

	const handleReset = (event) => {
		event.preventDefault();
		event.stopPropagation();
		setValue(initialValue);
	};

	const handleBlur = async (event) => {
		setError('');
		if (!isReadOnly && event?.target?.value !== initialValue) {
			await handleSubmit();
		}
	};

	const handleUpdateClient = async (newValue) => {
		if (header === 'industry') {
			insightFields[header] = 'IndustryId';
		}

		if (header === 'StateName') {
			insightFields[header] = 'StateId';
		}

		if (header === 'standardIndustryCode') {
			insightFields[header] = 'StandardIndustryCode';
		}

		// 'industry', 'isCurrent' and 'standardIndustryCode' are intentionally excluded here:
		// they always go through the insight branch below, and the backend derives their
		// Firestore values from that same Insight response — sending them again in `firebase`
		// would be a no-op (updateEndpoint doesn't read those keys off data.firebase).
		const firebaseFields = [
			'description',
			'referralPartner',
			'numberOfEmployees',
			'annualRevenue',
			'website',
		];
		const includeFirebase = firebaseFields.includes(header);

		if (insightFields[header]) {
			const fieldName = insightFields[header];

			const properties = {
				...(header !== 'isCurrent' &&
					header !== 'standardIndustryCode' && {
						[fieldName]: newValue,
					}),

				...(header === 'isCurrent' && {
					CurrentStatusId: newValue ? 0 : 1,
				}),
				...(header === 'standardIndustryCode' && {
					[fieldName]: newValue?.Code ?? null,
					[`${fieldName}Name`]: newValue?.Name
						? newValue.Name.split(`[${newValue.Code}]`)[0].trim()
						: null,
					[`${fieldName}AutoComplete`]: newValue
						? `${newValue.Code} ${newValue.Name}`
						: null,
					[`${fieldName}Id`]: newValue?.Id ?? null,
				}),
			};

			const insightResponse = await updateClientx({
				clientId,
				insight: properties,
				firebase: includeFirebase ? { [header]: newValue ?? null } : {},
			}).unwrap();
			console.log('insight res: ', insightResponse);

			if (insightResponse.error) {
				console.log('insight error: ', insightResponse.error);

				return;
			}
		} else if (includeFirebase) {
			await updateClientRow({
				clientId,
				docId: clientRow?.docId,
				properties: {
					[header]: newValue ?? null,
				},
			}).unwrap();
		}
	};

	const validationSchema = useMemo(
		() =>
			createValidationSchema([header], {
				skipEmail: false,
			}),
		[header],
	);

	const handleSubmit = async (newValue) => {
		setError('');

		let v = newValue ?? value ?? null;
		if (typeof v === 'string') {
			v = v.trim() || null;
		}

		console.log('Submitting value:', v);

		try {
			await validationSchema.validate({ [header]: v });

			await handleUpdateClient(v);
		} catch (e) {
			console.error(`Error updating client ${header}:`, e);

			dispatch(
				showToast({
					message: `Error updating client: ${
						e.data?.message ?? e.message ?? 'Unknown error'
					}`,
					error: true,
				}),
			);

			setError(e.data?.message ?? e.message ?? 'Unknown error');
		}
	};

	const setFloatValue = ({ floatValue }) => setValue(floatValue);

	const changeValue = (newValue) => {
		console.log('Changing value: ', newValue);
		setValue(newValue);
		handleSubmit(newValue);
	};

	const addressLabel = ['country', 'suburb', 'city', 'address', 'postcode'].includes(
		header?.toLowerCase(),
	);

	const drawComponent = () => {
		switch (header) {
			case 'industry':
				return (
					<IndustrySelect
						initialValue={clientRow?.industry}
						disabled={loading || disabled}
						onChange={changeValue}
					/>
				);
			case 'sourceOfBusiness':
				return (
					<SourceOfBusinessSelect
						initialValue={insight?.SourceOfBusinessId}
						disabled={loading || disabled}
						onChange={changeValue}
					/>
				);
			case 'standardIndustryCode':
				return (
					<AnzsicCodeSearch
						initialValue={value}
						onSelect={changeValue}
						disabled={loading || disabled}
					/>
				);
			case 'numberOfEmployees':
			case 'annualRevenue':
				return (
					<DrawNumeric
						prefix={header === 'annualRevenue' ? '$' : undefined}
						error={Boolean(error)}
						isLoading={loading}
						clickHandler={handleReset}
						onBlur={handleBlur}
						onValueChange={setFloatValue}
						disabled={loading}
						disableEdit={disabled}
						value={value ?? 0}
					/>
				);
			case 'isCurrent':
				return (
					<ClientStatusSelect
						hidelabel={true}
						clientStatus={value}
						onChange={changeValue}
						disabled={loading || disabled}
					/>
				);
			case 'referralPartner':
				return (
					<ReferralPartnerSelect
						value={value}
						disabled={loading || disabled}
						onChange={changeValue}
					/>
				);
			default:
				return (
					<Tooltip title={isReadOnly ? 'Currently uneditable' : ''} placement='top'>
						<TextField
							fullWidth
							slotProps={{
								input: {
									readOnly: isReadOnly,
									endAdornment: (
										<>
											{isUpdating ? (
												<CircularProgress size={20} />
											) : (
												!isReadOnly && (
													<ResetButton
														isLoading={loading}
														onMouseDown={handleReset}
													/>
												)
											)}
										</>
									),
								},
							}}
							value={value ?? ''}
							onChange={(e) => setValue(e.target.value)}
							error={Boolean(error)}
							onBlur={handleBlur}
							disabled={
								(disabled &&
									!['email', 'firstName', 'lastName'].includes(header)) ||
								isLoading
							}
						/>
					</Tooltip>
				);
		}
	};

	return (
		<>
			<FormLabel
				{...(addressLabel && {
					sx: {
						color: theme.palette.text.secondary,
						fontWeight: 400,
						fontSize: '0.75rem',
					},
				})}
			>
				{title}
			</FormLabel>
			{drawComponent()}
			{Boolean(error) && <FormHelperText error>{error}</FormHelperText>}
		</>
	);
};
